import React, { useState } from "react";
import { ArrowLeft, ArrowRight, Check, ChevronRight } from "lucide-react";
import OrganizationProfile from "./OrganizationProfile";
import SkuSelection from "./SkuSelection";
import SkuOverview from "./SkuOverview";
import HcmConfigurationSetup from "./HcmConfigurationSetup";
import FoundationalSetup from "./FoundationalSetup";
import ReviewSubmit from "./ReviewSubmit";
import {
  questionnaireStepTitles,
  QUESTIONNAIRE_STEPS_COUNT,
} from "../../../constants";

const QuestionnaireContainer = ({
  questionnaireData,
  setQuestionnaireData,
  nextStep,
  goBackToSystemSelection,
}) => {
  const [questionnaireStep, setQuestionnaireStep] = useState(0);
  const [questionnaireSubmitted, setQuestionnaireSubmitted] = useState(false);

  // Handle changes to org profile with explicit field updates
  const handleOrgProfileChange = (field, value) => {
    setQuestionnaireData((prev) => ({
      ...prev,
      orgProfile: {
        ...prev.orgProfile,
        [field]: value,
      },
    }));
  };

  // Handle changes to SKU selection
  const handleSkuChange = (sku) => {
    setQuestionnaireData((prev) => {
      const skuExists = prev.skuSelection.includes(sku);
      const newSkus = skuExists
        ? prev.skuSelection.filter((s) => s !== sku)
        : [...prev.skuSelection, sku];

      return {
        ...prev,
        skuSelection: newSkus,
      };
    });
  };

  // Questionnaire navigation
  const nextQuestionnaireStep = () => {
    if (questionnaireStep < QUESTIONNAIRE_STEPS_COUNT - 1) {
      setQuestionnaireStep(questionnaireStep + 1);
    } else if (questionnaireSubmitted) {
      // If at last step and already submitted, proceed to next step
      nextStep();
    } else {
      // If at last step but not submitted, mark as submitted
      setQuestionnaireSubmitted(true);
    }
  };

  const prevQuestionnaireStep = () => {
    if (questionnaireStep > 0) {
      setQuestionnaireStep(questionnaireStep - 1);
    } else {
      // If at first step, return to discovery flow (source system selection)
      goBackToSystemSelection(); // This function will take us back to system selection
    }
  };

  // Check if current step is complete
  const isCurrentStepComplete = () => {
    switch (questionnaireStep) {
      case 1: // SKU Selection
        return questionnaireData.skuSelection.length > 0;
      default:
        return true;
    }
  };

  // Render current questionnaire step
  const renderQuestionnaireStep = () => {
    switch (questionnaireStep) {
      case 0:
        return (
          <OrganizationProfile
            questionnaireData={questionnaireData}
            handleOrgProfileChange={handleOrgProfileChange}
          />
        );
      case 1:
        return (
          <SkuSelection
            questionnaireData={questionnaireData}
            handleSkuChange={handleSkuChange}
          />
        );
      case 2:
        return <SkuOverview questionnaireData={questionnaireData} />;
      case 3:
        return (
          <HcmConfigurationSetup
            questionnaireData={questionnaireData}
            setQuestionnaireData={setQuestionnaireData}
          />
        );
      case 4:
        return (
          <FoundationalSetup
            questionnaireData={questionnaireData}
            setQuestionnaireData={setQuestionnaireData}
          />
        );
      case 5:
        return (
          <ReviewSubmit
            questionnaireData={questionnaireData}
            questionnaireSubmitted={questionnaireSubmitted}
          />
        );
      default:
        return <div>Step not found</div>;
    }
  };

  return (
    <div>
      {/* Questionnaire Progress Steps */}
      <div className="mb-8">
        <ol className="flex items-center w-full">
          {questionnaireStepTitles.map((stepTitle, index) => (
            <li
              key={index}
              className={`flex items-center ${
                index < QUESTIONNAIRE_STEPS_COUNT - 1 ? "w-full" : ""
              }`}
            >
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
                  questionnaireStep > index
                    ? "bg-green-500 text-white"
                    : questionnaireStep === index
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                {questionnaireStep > index ? (
                  <Check size={16} />
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>
              <span
                className={`ml-2 text-sm font-medium ${
                  questionnaireStep === index
                    ? "text-blue-600"
                    : "text-gray-500"
                }`}
              >
                {stepTitle}
              </span>

              {index < QUESTIONNAIRE_STEPS_COUNT - 1 && (
                <div className="w-full flex items-center">
                  <div
                    className={`h-0.5 w-full mx-4 ${
                      questionnaireStep > index ? "bg-green-500" : "bg-gray-200"
                    }`}
                  ></div>
                  <ChevronRight className="shrink-0 text-gray-400" size={16} />
                </div>
              )}
            </li>
          ))}
        </ol>
      </div>

      {/* Current Questionnaire Step Content */}
      <div className="mb-8">{renderQuestionnaireStep()}</div>

      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t border-gray-200">
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center"
          onClick={prevQuestionnaireStep}
        >
          <ArrowLeft className="mr-2" size={16} />
          {questionnaireStep === 0 ? "Back to Discovery" : "Previous Step"}
        </button>

        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium ${
            isCurrentStepComplete()
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-400 cursor-not-allowed"
          } text-white rounded-md flex items-center`}
          onClick={nextQuestionnaireStep}
          disabled={!isCurrentStepComplete()}
        >
          {questionnaireStep === QUESTIONNAIRE_STEPS_COUNT - 1
            ? questionnaireSubmitted
              ? "Proceed"
              : "Submit Questionnaire"
            : "Next Step"}
          <ArrowRight className="ml-2" size={16} />
        </button>
      </div>
    </div>
  );
};

export default QuestionnaireContainer;
